import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView, Variants } from 'framer-motion';
import { 
  CheckCircle2, 
  MapPin, 
  Phone, 
  Clock, 
  Star, 
  Send, 
  Loader2, 
  ShieldCheck, 
  TrendingUp, 
  Moon, 
  ArrowLeft,
  Globe,
  ArrowRight
} from 'lucide-react';
import { FormData, Cities, ContactTimes } from './types';
import { postToOdoo } from './services/odooService';

type Language = 'ar' | 'en';

const translations = {
  ar: {
    dir: 'rtl' as const,
    nav: { apply: 'قدّم طلبك', ramadan: 'رمضان كريم' },
    hero: {
      subtitle: 'عرض تمويل السيارات رمضان 2026',
      title: 'امتلك سيارتك الجديدة بعائد تأجير ثابت 4.5%',
      desc: 'بمناسبة شهر رمضان المبارك، تقدم إجارة للتأجير التمويلي عرضاً خاصاً لتمويل السيارات بعائد ثابت 4.5% وفترة سداد تصل إلى خمس سنوات. عرض يمنحك وضوحاً في الالتزام، واستقراراً في التخطيط، ومرونة تناسب احتياجاتك، ضمن إطار تمويلي منظم وإجراءات سلسة.',
      validity: 'العرض سارٍ طوال شهر رمضان المبارك.',
      terms: 'خاضع للشروط والأحكام.',
      cta: 'قدّم طلبك الآن'
    },
    features: {
      header: 'مميزات العرض',
      items: [
        { title: 'عائد تأجير ثابت 4.5%', desc: 'طوال مدة العقد يمنحك وضوحاً واستقراراً.' },
        { title: '60 شهراً للسداد', desc: 'فترة سداد مرنة تصل إلى 5 سنوات تناسب احتياجاتك.' },
        { title: 'إجراءات منظمة', desc: 'إجراءات تمويل واضحة ومنظمة ضمن إطار متكامل.' },
        { title: 'خدمة مباشرة', desc: 'عبر فروعنا في عمّان، إربد، والعقبة.' }
      ]
    },
    form: {
      header: 'قدّم طلب التمويل',
      subtitle: 'يرجى تعبئة النموذج التالي، وسيتواصل معكم فريقنا في أقرب وقت:',
      name: 'الاسم الكامل',
      phone: 'رقم الهاتف',
      city: 'المدينة',
      vehicle: 'نوع السيارة',
      price: 'قيمة السيارة التقريبية',
      time: 'أفضل وقت للتواصل',
      agree: 'أوافق على الشروط والأحكام',
      submit: 'إرسال الطلب',
      success: 'تم الإرسال بنجاح!',
      error: 'حدث خطأ، يرجى المحاولة لاحقاً'
    },
    branches: {
      header: 'فروعنا',
      amman: 'عمان',
      irbid: 'اربد',
      aqaba: 'العقبة'
    },
    footer: {
      desc: 'شركة إجارة للتأجير التمويلي - الحلول التمويلية المبتكرة.',
      rights: 'جميع الحقوق محفوظة.'
    }
  },
  en: {
    dir: 'ltr' as const,
    nav: { apply: 'Apply Now', ramadan: 'Ramadan Kareem' },
    hero: {
      subtitle: 'Ramadan 2026 Auto Loan Offer',
      title: 'Own Your New Vehicle with a Fixed 4.5% Lease Rate',
      desc: 'During the holy month of Ramadan, Ejara Leasing Company presents a special Auto loan offer featuring a fixed rate of 4.5% and a Repayment period up to five years. This structured leasing solution offers financial clarity, predictable commitments, and flexible terms designed to support your planning with confidence and stability.',
      validity: 'The offer is valid throughout the holy month of Ramadan.',
      terms: 'Terms and conditions apply.',
      cta: 'Submit Your Application'
    },
    features: {
      header: 'Offer Highlights',
      items: [
        { title: 'Fixed 4.5% Lease Rate', desc: 'Throughout the contract term for full financial clarity.' },
        { title: '60 Months Repayment', desc: 'Flexible period up to 5 years to suit your financial needs.' },
        { title: 'Streamlined Process', desc: 'Transparent and organized financing procedures.' },
        { title: 'Direct Support', desc: 'Available at our branches in Amman, Irbid, and Aqaba.' }
      ]
    },
    form: {
      header: 'Submit Your Application',
      subtitle: 'Please complete the form below and our team will contact you shortly:',
      name: 'Full Name',
      phone: 'Mobile Number',
      city: 'City',
      vehicle: 'Vehicle Type',
      price: 'Approximate Vehicle Price',
      time: 'Preferred Contact Time',
      agree: 'I agree to the Terms and Conditions',
      submit: 'Submit',
      success: 'Submitted Successfully!',
      error: 'An error occurred, please try again'
    },
    branches: {
      header: 'Our Branches',
      amman: 'Amman',
      irbid: 'Irbid',
      aqaba: 'Aqaba'
    },
    footer: {
      desc: 'Ejara Leasing Company - Innovative Financing Solutions.',
      rights: 'All rights reserved.'
    }
  }
};

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>('ar');
  const t = translations[lang];

  useEffect(() => {
    document.documentElement.dir = t.dir;
    document.documentElement.lang = lang;
  }, [lang, t.dir]);

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    city: Cities.AMMAN,
    vehicleType: '',
    approxPrice: '',
    preferredTime: ContactTimes.MORNING,
    agreedToTerms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const heroRef = useRef<HTMLDivElement>(null);
  const isHeroInView = useInView(heroRef as React.RefObject<Element>, { amount: 0.2 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreedToTerms) return;
    setIsSubmitting(true);
    const success = await postToOdoo(formData);
    setIsSubmitting(false);
    if (success) {
      setSubmitStatus('success');
      setFormData({
        fullName: '', phone: '', city: Cities.AMMAN, vehicleType: '', 
        approxPrice: '', preferredTime: ContactTimes.MORNING, agreedToTerms: false
      });
    } else {
      setSubmitStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-[#fafafc] font-['Cairo'] selection:bg-[#00113d] selection:text-white relative overflow-x-hidden">
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-[100] px-6 md:px-12 transition-all duration-500 flex items-center justify-between ${
          scrolled ? 'bg-white/95 backdrop-blur-2xl border-b py-3 shadow-lg' : 'bg-transparent py-6 lg:py-10'
        }`}>
        <div className="flex items-center gap-6">
          <img 
            src="https://cdn.prod.website-files.com/686cbc7eeff3b1cc77862711/68985708b0abb7cfdd29ac73_Ejara%20logo-03.svg" 
            alt="Ejara" 
            className={`transition-all duration-500 h-10 lg:h-12 w-auto ${!scrolled && 'lg:h-20'}`}
          />
        </div>

        <div className="flex items-center gap-4 lg:gap-8">
          <button 
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
            className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl font-black text-sm hover:bg-slate-200 transition-colors"
          >
            <Globe className="w-4 h-4" />
            {lang === 'ar' ? 'English' : 'العربية'}
          </button>

          {scrolled && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-100">
                <Moon className="w-4 h-4 text-amber-500 fill-amber-500" />
                <span className="text-amber-900 font-black text-sm">{t.nav.ramadan}</span>
              </div>
              <button 
                onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#00113d] text-white px-6 py-3 rounded-xl font-black text-sm"
              >
                {t.nav.apply}
              </button>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen lg:h-[90vh] flex flex-col justify-center islamic-pattern pt-32 lg:pt-20 pb-10">
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center lg:text-start space-y-8">
              <div className="space-y-4">
                <h2 className="text-amber-600 font-black text-lg lg:text-2xl uppercase tracking-widest">{t.hero.subtitle}</h2>
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-[#00113d] leading-[1.1]">{t.hero.title}</h1>
                <p className="text-lg lg:text-xl text-slate-600 font-bold leading-relaxed max-w-2xl mx-auto lg:mx-0">{t.hero.desc}</p>
                <div className="flex flex-col sm:flex-row items-center gap-4 text-slate-400 font-black pt-4">
                  <span className="bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm">{t.hero.validity}</span>
                  <span className="text-sm opacity-60">{t.hero.terms}</span>
                </div>
              </div>
              <button 
                onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#00113d] text-white px-10 py-5 rounded-2xl font-black text-xl shadow-2xl hover:-translate-y-1 transition-all flex items-center justify-center gap-4 group mx-auto lg:mx-0"
              >
                {t.hero.cta}
                {lang === 'ar' ? <ArrowLeft className="w-6 h-6 group-hover:-translate-x-2 transition-transform" /> : <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />}
              </button>
            </motion.div>

            {/* Visual Part */}
            <div className="relative flex items-center justify-center pt-8 lg:pt-0">
              <motion.div 
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-full max-w-[280px] sm:max-w-[360px] md:max-w-[420px] lg:max-w-[460px] max-h-[70vh] shadow-2xl rounded-[2.5rem] overflow-hidden border-4 border-white bg-white"
              >
                <picture>
                  <source media="(min-width: 1024px)" srcSet="https://i.imgur.com/H5pd5qg.jpeg" />
                  <img src="https://i.imgur.com/KzxkTVs.jpeg" alt="Ejara Ramadan" className="w-full h-full object-contain block" />
                </picture>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Highlights */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-7xl text-center">
          <h2 className="text-3xl lg:text-5xl font-black text-[#00113d] mb-16">{t.features.header}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.features.items.map((feat, i) => (
              <div key={i} className="p-8 rounded-3xl bg-slate-50 border group hover:border-amber-200 transition-all">
                <div className="w-14 h-14 bg-[#00113d] text-white rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:bg-amber-500 transition-colors">
                  {[TrendingUp, Clock, ShieldCheck, MapPin][i] && React.createElement([TrendingUp, Clock, ShieldCheck, MapPin][i], { className: "w-7 h-7" })}
                </div>
                <h3 className="text-xl font-black text-[#00113d] mb-2">{feat.title}</h3>
                <p className="text-slate-500 font-bold">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 bg-[#f8f9ff]">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white p-8 lg:p-16 rounded-[3rem] shadow-2xl">
            <div className="text-center mb-12 space-y-4">
              <h2 className="text-3xl lg:text-5xl font-black text-[#00113d]">{t.form.header}</h2>
              <p className="text-slate-500 font-bold">{t.form.subtitle}</p>
            </div>

            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-black text-[#00113d]">{t.form.success}</h3>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="font-black text-sm text-[#00113d] px-1">{t.form.name}</label>
                    <input required name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-6 py-4 bg-slate-50 border rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="font-black text-sm text-[#00113d] px-1">{t.form.phone}</label>
                    <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-6 py-4 bg-slate-50 border rounded-xl" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="font-black text-sm text-[#00113d] px-1">{t.form.city}</label>
                    <select name="city" value={formData.city} onChange={handleChange} className="w-full px-6 py-4 bg-slate-50 border rounded-xl">
                      {Object.values(Cities).map(city => <option key={city} value={city}>{city}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="font-black text-sm text-[#00113d] px-1">{t.form.vehicle}</label>
                    <input required name="vehicleType" value={formData.vehicleType} onChange={handleChange} className="w-full px-6 py-4 bg-slate-50 border rounded-xl" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="font-black text-sm text-[#00113d] px-1">{t.form.price}</label>
                    <input required name="approxPrice" value={formData.approxPrice} onChange={handleChange} className="w-full px-6 py-4 bg-slate-50 border rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <label className="font-black text-sm text-[#00113d] px-1">{t.form.time}</label>
                    <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} className="w-full px-6 py-4 bg-slate-50 border rounded-xl">
                      {Object.values(ContactTimes).map(time => <option key={time} value={time}>{time}</option>)}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-3 cursor-pointer p-2">
                  <input type="checkbox" checked={formData.agreedToTerms} onChange={e => setFormData(f => ({...f, agreedToTerms: e.target.checked}))} className="w-5 h-5" />
                  <span className="font-bold text-slate-500">{t.form.agree}</span>
                </label>
                <button type="submit" disabled={isSubmitting || !formData.agreedToTerms} className="w-full py-5 bg-[#00113d] text-white rounded-2xl font-black text-xl flex items-center justify-center gap-3 shadow-xl">
                  {isSubmitting ? <Loader2 className="animate-spin" /> : <Send className="w-6 h-6" />}
                  {t.form.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Branches */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-7xl text-center">
          <h2 className="text-3xl lg:text-5xl font-black text-[#00113d] mb-12">{t.branches.header}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[{n: t.branches.amman, p: '06 553 4445'}, {n: t.branches.irbid, p: '02 725 3013'}, {n: t.branches.aqaba, p: '03 201 6070'}].map((b, i) => (
              <div key={i} className="p-10 rounded-3xl bg-slate-50 border flex flex-col items-center">
                <MapPin className="w-10 h-10 text-amber-500 mb-4" />
                <h4 className="text-2xl font-black mb-4">{b.n}</h4>
                <a href={`tel:${b.p.replace(/\s/g, '')}`} className="text-xl font-black text-[#00113d]" dir="ltr">{b.p}</a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#00081f] text-white py-16">
        <div className="container mx-auto px-6 text-center space-y-8">
          <img src="https://cdn.prod.website-files.com/686cbc7eeff3b1cc77862711/68985708b0abb7cfdd29ac73_Ejara%20logo-03.svg" alt="Ejara" className="h-10 mx-auto brightness-0 invert" />
          <p className="text-slate-400 font-bold max-w-md mx-auto">{t.footer.desc}</p>
          <div className="text-slate-600 font-black text-sm">&copy; 2026 إجارة. {t.footer.rights}</div>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      <AnimatePresence>
        {!isHeroInView && (
          <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} className="lg:hidden fixed bottom-6 left-6 right-6 z-[100]">
            <button onClick={() => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' })} className="w-full bg-[#00113d] text-white py-5 rounded-2xl font-black text-xl shadow-2xl">
              {t.hero.cta}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default App;